import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DatatableComponent, SelectionType } from '@swimlane/ngx-datatable';
import { TreeStatusEnum } from '@shared/modules/list/enums/tree-status.enum';
import { ListFilterComponent } from './components/list-filter/list-filter.component';
import { CustomTableColumn } from './interfaces/custom-table-column.interface';
import { ListConfig } from './interfaces/list-config.interface';
import { ListService } from './services/list.service';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { StorageService } from '@core/services/storage.service';
import { ListEvent, ListEventType } from '@shared/modules/list/model/list-event.model';
import { NavigateService } from '@shared/services/navigate.service';
import { ListNotShowColumnConfig } from './interfaces/list-not-show-column-config.interface';
import {
  buildListRowClass,
  decorateListColumns,
  handleTreeActivateEvent,
  identifyColumnByProp,
  sortColumnsByOrder
} from './helpers/list.helper';

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListComponent implements OnInit, AfterViewInit, OnDestroy {
  SelectionType = SelectionType;
  readonly columnsSort = sortColumnsByOrder;
  readonly identify = identifyColumnByProp;
  readonly config = input.required<ListConfig>();
  readonly emptyTemplate = input<TemplateRef<any>>();
  readonly filters = input<any>();
  readonly searchName = input<string>();
  readonly headerTemplate = input<TemplateRef<any>>();
  readonly isSearch = input(true);
  readonly selected = input<any[]>([]);
  readonly showToggleMenu = input<Function | undefined>(undefined);
  readonly hideFilters = input<string[] | undefined>(undefined);
  readonly headerHidden = input(false);
  readonly select = output<any>();
  readonly activate = output<any>();
  readonly unselect = output<void>();

  public readonly service = inject(ListService);
  public readonly t = inject(TranslateService);
  public readonly changes = inject(ChangeDetectorRef);
  private readonly store = inject(StorageService);
  private readonly n = inject(NavigateService);

  activateSub: Subscription;
  serviceSub: Subscription = new Subscription();
  scrollSub: Subscription = new Subscription();
  initedScrollPosition: boolean = false;

  @ViewChild('table') table: DatatableComponent;
  @ViewChild('listContainer') listContainer: ElementRef;
  @ViewChild('toggleMenu') toggleMenu: TemplateRef<any>;
  @ViewChild('checkbox') checkbox: TemplateRef<any>;
  @ViewChild('checkboxAll') checkboxAll: TemplateRef<any>;
  @ViewChild('toggleTree') toggleTree: TemplateRef<any>;
  @ViewChild('filtersComponent') filtersComponent: ListFilterComponent;
  @ViewChild('columnsSettings') columnsSettings: TemplateRef<any>;

  constructor() {
    effect(() => {
      this.service.config = this.config();
    });
  }

  ngOnInit(): void {
    this.service.clearService();
    this.service.config = this.config();
    this.service.config.limit = this.service.PerPage as number;
    this.listenListServiceEvents();
    this.changes.detectChanges();
  }

  ngAfterViewInit() {
    this.service.table = this.table;
    this.service.filters = this.filtersComponent;
    this.service.readUrlParams();
    this.service.setInitSorting();
    this.service.getRows();
    this.setToggleMenuAndTreeMenuAndCheckboxes();
    this.setHiddenFilters();
    this.changes.detectChanges();
  }

  setHiddenFilters() {
    if (this.hideFilters()) {
      this.service.hideFilters = this.hideFilters();
    }
  }

  listenListServiceEvents() {
    this.serviceSub = this.service.eventEmitter.subscribe((e: ListEvent<any>) => {
      switch (e.type) {
        case ListEventType.END_GET_ROWS:
          this.handleScrollPosition();
          break;
      }
    });
  }

  handleScrollPosition() {
    const scrollBehavior = this.service.consumeNextScrollBehavior();

    if (scrollBehavior === 'reset') {
      this.resetScrollPosition();
      return;
    }

    this.initScrollPosition();
  }

  setToggleMenuAndTreeMenuAndCheckboxes() {
    decorateListColumns(this.service.config.columns, {
      toggleMenu: this.toggleMenu,
      columnsSettings: this.columnsSettings,
      toggleTree: this.toggleTree,
      checkbox: this.checkbox,
      checkboxAll: this.checkboxAll
    });

    this.table.headerComponent.headerHeight = this.table.headerComponent.headerHeight; // refresh header templates
    this.changes.detectChanges();
  }

  onSort(e) {
    this.service.setSort(e.column.sortField, e.newValue, e.column, this.config().columns);
  }

  onTreeAction(e) {
    if (e.row.treeStatus === TreeStatusEnum.LOADING) {
      return;
    }
    this.config().treeAction?.treeActionFunction ? this.config().treeAction.treeActionFunction(e) : null;
  }

  /**
   * check if click is on tree toggle cell - if yes block activate event
   * @param e
   */
  onActivate(e: { type: string; row: any; column: CustomTableColumn; event }) {
    e = handleTreeActivateEvent(e);
    this.activate.emit(e);
  }

  treeDisabled(event) {
    return this.config().treeAction?.treeDisabledFunction
      ? this.config().treeAction.treeDisabledFunction(event)
      : false;
  }

  singleSelectCheck(row: any) {
    this.unselect.emit();
    return this.selected().indexOf(row) === -1;
  }

  getRowClass(row) {
    return buildListRowClass(
      row,
      this.service.config.rowClass,
      this.service.config.rowClassFunction,
      this.treeDisabled({ row })
    );
  }

  initScrollPosition() {
    if (this.initedScrollPosition) {
      this.setScrollPosition();
      return;
    }
    this.initedScrollPosition = true;

    const current = this.n.currentUrl.split('?')[0] || this.n.currentUrl;
    const secondPreviousUrl = this.n.secondPreviousUrl.split('?')[0] || this.n.secondPreviousUrl;

    if (secondPreviousUrl.indexOf(current) === -1 && current.indexOf(secondPreviousUrl) === -1) {
      this.listenScroll();
      return;
    }

    const historyScroll = this.store.get(this.service.config.listName + '-scroll');
    if (!historyScroll) {
      this.listenScroll();
      return;
    }

    this.setScrollPositionToBasePanel(historyScroll);
  }

  setScrollPosition() {
    if (!this.service.config.listName) {
      return;
    }
    const historyScroll = this.store.get(this.service.config.listName + '-scroll');
    this.setScrollPositionToBasePanel(historyScroll);
  }

  setScrollPositionToBasePanel(historyScroll) {
    if (!historyScroll) {
      return;
    }
    let counts = 0;

    const intervalToLoadRows = setInterval(() => {
      const rowsLength = document.querySelectorAll('.datatable-row-wrapper').length; // check if all rows are already rendered in DOM
      if (counts > 40 || this.table.pageSize <= rowsLength) {
        clearInterval(intervalToLoadRows);
        this.listContainer.nativeElement.querySelector('.datatable-body').scrollTop = historyScroll;
        this.listenScroll();
      }
      counts++;
    }, 100);
  }

  resetScrollPosition() {
    this.initedScrollPosition = true;
    this.table?.bodyComponent?.updateOffsetY(0);

    const tableBody = this.listContainer?.nativeElement?.querySelector('.datatable-body');
    if (tableBody) {
      tableBody.scrollTop = 0;
    }

    if (this.service.config?.listName) {
      this.store.put(this.service.config.listName + '-scroll', 0);
    }

    this.listenScroll();
  }

  listenScroll() {
    this.scrollSub.unsubscribe();
    this.scrollSub = this.table.bodyComponent.scroll.pipe(debounceTime(120 /* ms */)).subscribe(() => {
      const scroll = this.table.bodyComponent.offsetY;
      this.store.put(this.service.config.listName + '-scroll', scroll);
    });
  }

  onColumnShowChange(column: CustomTableColumn) {
    if (column.canNotShow === false) return;

    column.notShow = !column.notShow;
    this.changes.detectChanges();
    this.updateNotShowColumnsInStorage(column);
  }

  updateNotShowColumnsInStorage(column: CustomTableColumn) {
    const notShowColumnConfig: ListNotShowColumnConfig = this.store.NotShowColumns || {};

    if (
      notShowColumnConfig[this.service.config.listName] &&
      notShowColumnConfig[this.service.config.listName].userId === this.store.UserPerson.id
    ) {
      const index = notShowColumnConfig[this.service.config.listName].columns.indexOf(column.prop as string);
      if (column.notShow) {
        // add column prop to storage
        if (index === -1) {
          notShowColumnConfig[this.service.config.listName].columns.push(column.prop as string);
        }
      } else {
        if (index !== -1) {
          // remove column prop from storage
          notShowColumnConfig[this.service.config.listName].columns.splice(index, 1);
        }
      }
    } else {
      notShowColumnConfig[this.service.config.listName] = {
        userId: this.store.UserPerson.id,
        columns: [column.prop as string]
      };
    }
    this.store.NotShowColumns = notShowColumnConfig;
    this.service.eventEmitter.emit({ type: ListEventType.CONFIG_COLUMNS });
  }

  ngOnDestroy() {
    this.service.clearService();
    this.initedScrollPosition = false;
    this.activateSub ? this.activateSub.unsubscribe() : null;
    this.scrollSub.unsubscribe();
    this.serviceSub.unsubscribe();
  }
}
